import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, ZoomIn, ZoomOut, RotateCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImageUploadWithCrop = ({ 
  currentImage, 
  onImageChange, 
  shape = 'oval', // 'oval', 'square', 'rectangle'
  disabled = false,
  label = "Profile Photo"
}) => {
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(currentImage || null);
  const [uploading, setUploading] = useState(false);
  const [cropSettings, setCropSettings] = useState({
    zoom: 1,
    rotation: 0,
    position: { x: 0, y: 0 }
  });
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setShowCropDialog(true);
      setCropSettings({ zoom: 1, rotation: 0, position: { x: 0, y: 0 } });
    };
    reader.readAsDataURL(file);
  };

  const handleMouseDown = (e) => {
    if (!imageRef.current) return;
    isDragging.current = true;
    dragStart.current = {
      x: e.clientX - cropSettings.position.x,
      y: e.clientY - cropSettings.position.y
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    setCropSettings(prev => ({
      ...prev,
      position: {
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y
      }
    }));
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleCropAndSave = () => {
    if (!imageRef.current) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size based on shape
    const size = shape === 'rectangle' ? { width: 300, height: 200 } : 300;
    canvas.width = shape === 'rectangle' ? size.width : size;
    canvas.height = shape === 'rectangle' ? size.height : size;

    // Create circular/oval clipping path for non-rectangle shapes
    if (shape !== 'rectangle') {
      ctx.beginPath();
      if (shape === 'oval') {
        // Draw ellipse
        ctx.ellipse(canvas.width / 2, canvas.height / 2, canvas.width / 2, canvas.height / 2, 0, 0, 2 * Math.PI);
      } else {
        // Draw circle for square
        ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, 2 * Math.PI);
      }
      ctx.clip();
    }

    // Draw image with transformations
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((cropSettings.rotation * Math.PI) / 180);
    ctx.scale(cropSettings.zoom, cropSettings.zoom);
    
    const img = imageRef.current;
    // Scale the image to fit the canvas, matching the preview behavior
    const displayScale = Math.min(canvas.width / img.naturalWidth, canvas.height / img.naturalHeight);
    const drawW = img.naturalWidth * displayScale;
    const drawH = img.naturalHeight * displayScale;
    // Position offsets are in preview pixels — scale them relative to canvas/preview ratio
    const previewSize = shape === 'rectangle' ? 300 : 250; // matches the CSS crop overlay size
    const canvasScale = canvas.width / previewSize;
    const offsetX = (cropSettings.position.x * canvasScale) / cropSettings.zoom;
    const offsetY = (cropSettings.position.y * canvasScale) / cropSettings.zoom;
    ctx.drawImage(img, -drawW / 2 + offsetX, -drawH / 2 + offsetY, drawW, drawH);
    ctx.restore();

    // Convert to blob and upload to S3
    canvas.toBlob(async (blob) => {
      if (!blob) {
        toast.error('Failed to process image');
        return;
      }
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', blob, 'profile-photo.jpg');
        formData.append('folder', 'profile-photos');
        // If current image is an S3 URL, pass it so backend deletes the old file
        if (currentImage && currentImage.startsWith('https://') && currentImage.includes('.s3.')) {
          formData.append('replace_url', currentImage);
        }
        const res = await axios.post(`${API}/upload/image`, formData);
        const s3Url = res.data.url;
        setImagePreview(s3Url);
        onImageChange(s3Url);
        setShowCropDialog(false);
      } catch (err) {
        console.error('S3 upload failed:', err);
        toast.error('Failed to upload photo. Please try again.');
      } finally {
        setUploading(false);
      }
    }, 'image/jpeg', 0.9);
  };

  const handleRemove = () => {
    setImagePreview(null);
    onImageChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getShapeClass = () => {
    switch (shape) {
      case 'oval':
        return 'rounded-full aspect-square';
      case 'square':
        return 'rounded-lg aspect-square';
      case 'rectangle':
        return 'rounded-lg aspect-[3/2]';
      default:
        return 'rounded-full aspect-square';
    }
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-stone-700">{label}</label>
      
      <div className="flex items-start gap-4">
        {/* Preview */}
        <div className={`relative ${getShapeClass()} w-32 bg-stone-100 border-2 border-stone-200 overflow-hidden flex items-center justify-center`}>
          {imagePreview || currentImage ? (
            <>
              <img 
                src={imagePreview || currentImage} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
              {!disabled && (
                <button
                  onClick={handleRemove}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  type="button"
                >
                  <X size={14} />
                </button>
              )}
            </>
          ) : (
            <Upload className="text-stone-400" size={32} />
          )}
        </div>

        {/* Upload Button */}
        {!disabled && (
          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="w-full"
            >
              <Upload size={16} className="mr-2" />
              {imagePreview || currentImage ? 'Change Photo' : 'Upload Photo'}
            </Button>
            <p className="text-xs text-stone-500 mt-2">
              {shape === 'oval' ? 'Recommended: Square image, will be cropped to oval' :
               shape === 'square' ? 'Recommended: Square image' :
               'Recommended: Landscape image (3:2 ratio)'}
            </p>
            <p className="text-xs text-stone-400">Max size: 5MB. Formats: JPG, PNG, WEBP</p>
          </div>
        )}
      </div>

      {/* Crop Dialog */}
      <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Adjust Your Photo</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Crop Preview */}
            <div 
              className="relative bg-stone-900 rounded-lg overflow-hidden"
              style={{ height: '400px' }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <img
                  ref={imageRef}
                  src={imagePreview}
                  alt="Crop preview"
                  className="max-w-full max-h-full cursor-move"
                  style={{
                    transform: `scale(${cropSettings.zoom}) rotate(${cropSettings.rotation}deg) translate(${cropSettings.position.x}px, ${cropSettings.position.y}px)`,
                    transition: isDragging.current ? 'none' : 'transform 0.1s'
                  }}
                  onMouseDown={handleMouseDown}
                  draggable={false}
                />
              </div>

              {/* Crop overlay */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-black bg-opacity-50" />
                <div 
                  className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-4 border-white ${
                    shape === 'oval' ? 'rounded-full' : shape === 'square' ? 'rounded-lg' : 'rounded-lg'
                  }`}
                  style={{
                    width: shape === 'rectangle' ? '300px' : '250px',
                    height: shape === 'rectangle' ? '200px' : '250px',
                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
                  }}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <ZoomIn size={16} />
                    Zoom
                  </label>
                  <span className="text-sm text-stone-600">{Math.round(cropSettings.zoom * 100)}%</span>
                </div>
                <Slider
                  value={[cropSettings.zoom]}
                  onValueChange={([value]) => setCropSettings(prev => ({ ...prev, zoom: value }))}
                  min={0.5}
                  max={3}
                  step={0.1}
                  className="w-full"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <RotateCw size={16} />
                    Rotation
                  </label>
                  <span className="text-sm text-stone-600">{cropSettings.rotation}°</span>
                </div>
                <Slider
                  value={[cropSettings.rotation]}
                  onValueChange={([value]) => setCropSettings(prev => ({ ...prev, rotation: value }))}
                  min={-180}
                  max={180}
                  step={1}
                  className="w-full"
                />
              </div>
            </div>

            <p className="text-sm text-stone-500 text-center">
              Drag the image to reposition • Use controls to zoom and rotate
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCropDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCropAndSave} className="bg-[#1A4D2E]" disabled={uploading}>
              {uploading ? <><Loader2 size={16} className="mr-2 animate-spin" />Uploading...</> : 'Save Photo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageUploadWithCrop;
